import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { StyledButton } from "@insite/mobius/Clickable";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductPrice, { ProductPriceStyles } from "@sunSource/Overrides/Components/ProductPrice";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    showLineNotes?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    addToListGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    subTotalPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPriceGridItem?: GridItemProps;
    productAvailabilityGridItem?: GridItemProps;
    quantityAddToListGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    rightGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    quantityAndAddToListContainer?: InjectableCss;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    buttonWrapper?: InjectableCss;
    buttonDevider?: InjectableCss;
    customBtn?: InjectableCss;
    editBtn?: InjectableCss;
    qtyOrdered?: InjectableCss;
    purchase?: InjectableCss;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            display: flex;
            padding: 0px;
            margin-bottom: 15px;
            > ${GridOffset} {
                width: 100%;
                margin: 0px;
            }
        `,
    },
    productImageGridItem: {
        width: [2, 2, 3, 3, 3],
        css: css`
            background: white;
            position: relative;
            padding-left: 0px;
            a {
                background: white;
                height: 100%;
                border: 1px solid ${getColor("common.border")};
                border-radius: 4px;
                align-items: center;
                justify-content: center;
                display: flex;
                box-shadow: 0 3px 6px 0 rgba(42, 51, 62, 0.1), 0 0 1px 0 rgba(42, 51, 62, 0.4);
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: {
        width: [10, 10, 6, 6, 6],
        css: css`
            flex-direction: column;
            span {
                font-size: 12px;
                font-weight: 400;
                color: gray;
            }
            a {
                margin-bottom: 10px;
                span {
                    font-size: 11px;
                    font-weight: 600;
                    line-height: inherit;
                }
            }
        `,
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                css: css`
                    color: ${getColor("primary")};
                    font-weight: bold;
                    line-height: 22px;
                `,
            },
        },
    },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            background: red;
        `,
    },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            // padding: 6px 6px 0px 6px;
            // min-height: 89px;
            // >${StyledButton}{
            //     font-size: 16px;
            // }
            > ${StyledButton} {
                > ${TypographyStyle} {
                    font-size: 16px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            min-height: unset;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityAndAddToListContainer: {
        css: css`
            // border: 1px solid red;
            // margin-bottom: 10px;
            // display: flex;
        `,
    },
    quantity: {
        editableQuantityTextField: {
            labelProps: {
                variant: "legend",
                css: css`
                    font-size: 14px;
                    text-transform: uppercase;
                    font-weight: 700;
                    color: ${getColor("secondary")};
                `,
            },
        },
    },
    quantityGridItem: {
        width: 12,
        css: css`
            padding-right: 0;
            display: flex;
            flex-direction: row;
            > ${FormFieldStyle} {
                width: 4.8rem;
                min-width: 4.8rem;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: row;
                            flex-basis: 50%;
                            max-width: 50%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    addToListGridItem: {
        width: 12,
        css: css`
            padding: 2px 0px 15px 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 15px 0px 15px 15px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            > ${StyledButton} {
                margin-top: 0px;
            }
        `,
    },
    extendedUnitNetPriceGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
        `,
    },
    subTotalPriceGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-top: 0px;
            > ${TypographyStyle} {
                color: ${getColor("secondary")};
                font-size: 14px;
                font-weight: 400;
                display: flex;
                flex-direction: column;
                > span {
                    &:nth-child(2) {
                        font-size: 16px;
                        font-weight: 700;
                        line-height: 21px;
                    }
                }
            }
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            background: blue;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        flex-direction: row;
                        padding-left: 0px;
                    `,
                    css`
                        flex-direction: row;
                        padding-left: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    productPriceGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-top: 0px;
            text-align: left !important;
        `,
    },
    productPrice: {
        priceWrapper: {
            css: css`
                > span {
                    font-size: 16px;
                    color: ${getColor("secondary")};
                    &:last-child {
                        white-space: break-spaces;
                    }
                }
            `,
        },
        priceLabelText: {
            css: css`
                font-size: 14px;
                color: ${getColor("secondary")};
                text-transform: capitalize;
                font-weight: 400;
                padding: 0px;
            `,
        },
        quoteMessageWrapper: {
            css: css`
                button {
                    display: flex !important;
                }
            `,
        },
    },
    productAvailabilityGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            padding-top: 0px;
            padding-bottom: 0px;
            > ${TypographyStyle} {
                font-size: 14px;
            }
        `,
    },
    productAvailability: {
        messageText: {
            css: css`
                font-size: 14px;
            `,
        },
        availabilityByWarehouseLinkGridItem: {
            css: css`
                > ${StyledButton} {
                    > ${TypographyStyle} {
                        font-size: 14px;
                    }
                }
            `,
        },
    },
    // quantityAddToListGridItem: {
    //     width: [12, 12, 12, 4, 4],
    //     css: css`
    //     background: green;
    //         flex-direction: column;
    //         > div[class^="GridWrapper"] {
    //             > ${GridOffset} {
    //                 flex-wrap: wrap;
    //                 ${({ theme }: { theme: BaseTheme }) =>
    //                     breakpointMediaQueries(
    //                         theme,
    //                         [
    //                             null,
    //                             null,
    //                             css`
    //                                 flex-wrap: nowrap;
    //                             `,
    //                             null,
    //                             null,
    //                         ],
    //                         "max",
    //                     )}
    //             }
    //         }
    //         ${({ theme }: { theme: BaseTheme }) =>
    //             breakpointMediaQueries(
    //                 theme,
    //                 [
    //                     null,
    //                     null,
    //                     css`
    //                         flex-direction: row;
    //                     `,
    //                     null,
    //                     null,
    //                 ],
    //                 "max",
    //             )}
    //     `,
    // },
    addToListLink: {
        css: css`
            margin-top: 20px;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            background: pink;
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: {
        width: 12,
        css: css`
            background: orange;
            padding: 0px 10px 10px 10px;
        `,
    },
    cartLineNotes: {
        label: {
            css: css`
                button {
                    color: ${getColor("primary")};
                    font-size: 16px;
                    letter-spacing: 0.14px;
                    font-weight: 600;
                    background: none;
                    border: none;
                    padding: 0;
                    cursor: pointer;
                }
            `,
        },
    },
    rightGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: end;
            align-items: end;
            gap: 2px;
            div {
                padding: 0px;
            }
            legend {
                display: none;
            }
            section {
                > div {
                    text-align: right;
                    line-height: 20px;
                    margin-bottom: 3px;
                    white-space: normal;
                    > span[data-test-selector="quoteRequiredText"] {
                        white-space: nowrap;
                        font-size: 13px;
                    }
                }
                span {
                    font-size: 15px;
                    color: #373737;
                }
                button {
                    display: none;
                }
            }
        `,
        width: [12, 12, 3, 3, 3],
    },
    removeCartLineIcon: { src: XCircle },
    binInformationsGridItem: {
        width: 12,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            gap: 5px;
            justify-content: space-between;
            align-items: baseline;
            span {
                width: max-content;
                font-size: 13px;
            }
        `,
    },
    buttonDevider: {
        css: css`
            width: 1px;
            height: 14px;
            background: gray;
            position: relative;
        `,
    },
    customBtn: {
        css: css`
            min-height: auto;
            padding: 0px;
            height: fit-content;
            background: transparent !important;
            border: none;
            > span {
                font-weight: 400;
                color: red;
            }
        `,
    },
    editBtn: {
        css: css`
            min-height: auto;
            padding: 0px;
            height: fit-content;
            background: transparent !important;
            border: none;
            > span {
                font-weight: 400;
                color: red;
            }
            &::after {
                width: 1px;
                height: 14px;
                position: absolute;
                right: 0px;
                background: gray;
                top: 6px;
            }
        `,
    },
    qtyOrdered: {
        css: css`
            position: absolute;
            top: 0px;
            right: 0px;
            background: #373737;
            border-radius: 2px;
            padding: 2px;
            color: white;
            min-width: 25px;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 13px;
        `,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    showPricing,
    showVmiBinDetails,
    cartUrl,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    return (
        <>
            <GridContainer
                {...styles.container}
                data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            >
                <GridItem {...styles.productImageGridItem}>
                    {showImage && <ProductImage product={cartLine} extendedStyles={styles.productImage} />}
                    {/* <CartLineQuantity
                        cart={cart}
                        editable={true}
                        onQtyOrderedChange={qtyOrderedChangeHandler}
                        extendedStyles={styles.quantity}
                    /> */}
                    <StyledWrapper {...styles.qtyOrdered}>
                        {translate("Qty")} {`(${cartLine.qtyOrdered})`}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.cartLineInfoGridItem}>
                    {showProductTitle && (
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    )}

                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        showProductNumber={showProductNumber}
                    />

                    {errorMessages.length > 0 && (
                        <StyledWrapper>
                            {errorMessages.map((message, index) => (
                                <Typography
                                    {...styles.cartLineErrorMessageText}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    data-test-selector="cartline_errorMessage"
                                >
                                    {message}
                                </Typography>
                            ))}
                        </StyledWrapper>
                    )}
                </GridItem>
                <GridItem {...styles.rightGridItem}>
                    <StyledWrapper>
                        {!cart.cartNotPriced && (
                            <GridItem {...styles.productPriceGridItem}>
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    showInvalidPriceMessage={true}
                                    isCartPage={true}
                                    isOnePageCheckout={true}
                                    extendedStyles={styles.productPrice}
                                />
                            </GridItem>
                        )}
                        {showPricing &&
                            promotions.map(promotion => (
                                <Typography {...styles.promotionNameText} key={promotion.id}>
                                    {promotion.name}
                                </Typography>
                            ))}

                        {showInventoryAvailability &&
                            !cartLine.quoteRequired &&
                            !(
                                !cartLine.allowZeroPricing &&
                                cartLine.pricing &&
                                getUnitNetPrice(cartLine.pricing, 1).price === 0
                            ) && (
                                <GridItem {...styles.productAvailabilityGridItem}>
                                    {cartLine.availability?.message !== null ? (
                                        <Typography>{translate("Inventory Status:")}&nbsp;</Typography>
                                    ) : null}
                                    <ProductAvailability
                                        productId={cartLine.productId!}
                                        availability={cartLine.availability!}
                                        unitOfMeasure={cartLine.unitOfMeasure}
                                        trackInventory={cartLine.trackInventory}
                                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                        extendedStyles={styles.productAvailability}
                                    />
                                </GridItem>
                            )}
                        {cartLine.isSubscription && cartLine.productSubscription && (
                            <ProductDeliveryScheduleButton
                                subscription={
                                    cartLine.properties.productSubscription
                                        ? JSON.parse(cartLine.properties.productSubscription)
                                        : cartLine.productSubscription
                                }
                                disabled={
                                    !cart.canModifyOrder ||
                                    cartLine.isPromotionItem ||
                                    !cartLine.qtyOrdered ||
                                    cartLine.qtyOrdered <= 0
                                }
                                onSave={saveScheduleHandler}
                                extendedStyles={styles.deliveryScheduleButton}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Link {...styles.editBtn} href={cartUrl}>
                            <Typography>{translate("Edit Qty")}</Typography>
                        </Link>
                        <StyledWrapper {...styles.buttonDevider}></StyledWrapper>
                        {showRemoveAction && (
                            <Link onClick={removeCartLineClickHandler} {...styles.customBtn}>
                                <Typography>{translate("Delete")}</Typography>
                            </Link>
                        )}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
